import { motion } from 'motion/react';
import type { ReactNode } from 'react';

// Easing lifted from the antigravity.google reference brief — the standard
// "ease-out-ish" curve for anything that moves position or opacity here.
const EASE = [0.165, 0.84, 0.44, 1] as const;

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.028, delayChildren: 0.02 } },
};

const word = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.42, ease: EASE } },
};

/**
 * The large page title, revealed word by word on mount.
 *
 * Split at word boundaries, not characters — character-splitting a heading
 * is the classic way to break screen readers. Thai script does not use
 * spaces between words, so `children.split(' ')` on a Thai heading (see
 * src/lib/i18n/th.ts) naturally returns the whole string as a single
 * element: one animated node instead of several, not a broken split. Still
 * correct, just a different (and equally intentional) shape of the same
 * animation.
 *
 * `MotionConfig reducedMotion="user"` at the app root already turns this
 * into a plain opacity fade for anyone with `prefers-reduced-motion` set —
 * nothing extra to do here.
 */
export function PageHeading({ children, subtitle }: { children: string; subtitle?: ReactNode }) {
  const words = children.split(' ');
  return (
    <div className="page-heading">
      <motion.h1 variants={container} initial="hidden" animate="show" aria-label={children}>
        {words.map((w, i) => (
          <motion.span
            // Index is stable here — this list is static per render, never
            // reordered or filtered.
            key={i}
            variants={word}
            className="page-heading-word"
            aria-hidden="true"
          >
            {w}
          </motion.span>
        ))}
      </motion.h1>
      {subtitle && <p className="page-heading-sub">{subtitle}</p>}
    </div>
  );
}
